import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Spinner, Modal } from "@deere/ux.uxframe-react";
import wtyReportApi from "../../services/warrantyReportAxios";
import { ArrowBack, FileDownload } from "@mui/icons-material";
import ErrorToast from "../common/ErrorToast";

const VIRTUALIZATION_THRESHOLD = 100;
const ROW_HEIGHT = 40;
const OVERSCAN_COUNT = 10;

/* const downloadCsv = (columns, rows, filename) => {
  const visibleCols = columns.filter((c) => VISIBLE_COLUMN_KEYS.has(c.key) && c.key !== "claims");
  const header = visibleCols.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((row) =>
    visibleCols.map((c) => {
      const val = row[c.key] ?? "";
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(",")
  ).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}; */

const COLUMNS = [
  { key: "pin17",              label: "PIN" },
  { key: "ctgryNm",            label: "Model" },
  { key: "mfgUnit",            label: "MFG Unit" },
  { key: "mru",                label: "Branch" },
  { key: "combinedTerritory",  label: "Service Territory" },
  { key: "servTerritoryDsc",   label: "TCSM" },
  { key: "servDlrNum",         label: "Dealer Account" },
  { key: "dealerName",         label: "Dealer Name" },
  { key: "wrntyEndDt",         label: "Warranty End Date" },
  { key: "fctryBuildDt",       label: "Build Date" },
  { key: "dlvrRcptDt",         label: "Delivery Date" },
  { key: "newUsedInd",         label: "Machine Status" },
  { key: "dlrInvDt",           label: "Invoice Date" },
  { key: "totalWrntySpend",    label: "Total Warranty Cost ($)" },
  { key: "claims",             label: "Claims" },
  { key: "prodId",             label: "Product ID" },
  { key: "pin13",              label: "PIN 13" },
  { key: "srlNum",             label: "Serial #" },
  { key: "soldByDlrNum",       label: "Sold By Dealer #" },
  { key: "dlrInvNum",          label: "Dealer Invoice" },
  { key: "acctNm",             label: "Account Name" },
  { key: "nm1Txt",             label: "Name 1" },
  { key: "nm2Txt",             label: "Name 2" },
  { key: "firstNm",            label: "First Name" },
  { key: "lastNm",             label: "Last Name" },
  { key: "street1Txt",         label: "Street 1" },
  { key: "street2Txt",         label: "Street 2" },
  { key: "city",               label: "City" },
  { key: "stateProvCd",        label: "State" },
  { key: "postalCd",           label: "Postal Code" },
  { key: "iso2CntryCd",        label: "Country" },
  { key: "businessPhone",      label: "Business Phone" },
  { key: "serviceDivision",    label: "Service Division" },
  { key: "serviceTerritory",   label: "Service Territory (raw)" },
  { key: "mstrWrntyDsc",       label: "Master Warranty" },
  { key: "mstrWrntyTypNm",     label: "Warranty Type" },
  { key: "fctryCrncyCd",       label: "Factory Currency" },
  { key: "fctryPrncyCd",       label: "Factory Price Currency" },
  { key: "locClaimCrncyCd",    label: "Local Claim Currency" },
  { key: "pstrAddr1",          label: "Postal Address 1" },
  { key: "pstrAddr2",          label: "Postal Address 2" },
  { key: "pcity",              label: "Postal City" },
  { key: "pstAbbr",            label: "Postal State" },
  { key: "ppstlCd",            label: "Postal ZIP" },
  { key: "pcountry",           label: "Postal Country" },
  { key: "ctgrCdFlat",         label: "Category Codes" },
];

const VISIBLE_COLUMN_KEYS = new Set([
  "pin17",
  "ctgryNm",
  "mfgUnit",
  "mru",
  "combinedTerritory",
  "servTerritoryDsc",
  "servDlrNum",
  "dealerName",
  "wrntyEndDt",
  "fctryBuildDt",
  "dlvrRcptDt",
  "newUsedInd",
  "dlrInvDt",
  "totalWrntySpend",
  "claims",
]);

const getDealerName = (row) => row.acctNm || "";

const formatWarrantyCost = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const DealerReport = () => {
  const history = useHistory();
  const { state } = useLocation();
  const { pin, searchResults = [], searchCriteria } = state || {};
  const [claimsLoadingIndex, setClaimsLoadingIndex] = useState(null);
  const [pin17LoadingIndex, setPin17LoadingIndex] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportModal, setExportModal] = useState({ open: false, success: false });
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [scrollMetrics, setScrollMetrics] = useState({ scrollTop: 0, containerHeight: 0 });

  const tableScrollRef = useRef(null);
  const mirrorScrollRef = useRef(null);
  const mirrorInnerRef = useRef(null);

  useEffect(() => {
    const tableEl = tableScrollRef.current;
    const mirrorEl = mirrorScrollRef.current;
    const mirrorInner = mirrorInnerRef.current;

    const updateLayout = () => {
      if (!tableEl) {
        return;
      }

      if (mirrorInner) {
        mirrorInner.style.width = tableEl.scrollWidth + "px";
      }

      setScrollMetrics((current) => {
        const nextMetrics = {
          scrollTop: tableEl.scrollTop,
          containerHeight: tableEl.clientHeight,
        };

        return current.scrollTop === nextMetrics.scrollTop && current.containerHeight === nextMetrics.containerHeight
          ? current
          : nextMetrics;
      });
    };

    updateLayout();

    const onTableScroll = () => {
      if (mirrorEl) {
        mirrorEl.scrollLeft = tableEl.scrollLeft;
      }

      setScrollMetrics((current) => {
        const nextMetrics = {
          scrollTop: tableEl?.scrollTop ?? 0,
          containerHeight: tableEl?.clientHeight ?? 0,
        };

        return current.scrollTop === nextMetrics.scrollTop && current.containerHeight === nextMetrics.containerHeight
          ? current
          : nextMetrics;
      });
    };
    const onMirrorScroll = () => { if (tableEl) tableEl.scrollLeft = mirrorEl.scrollLeft; };

    tableEl?.addEventListener("scroll", onTableScroll);
    mirrorEl?.addEventListener("scroll", onMirrorScroll);
    window.addEventListener("resize", updateLayout);

    return () => {
      tableEl?.removeEventListener("scroll", onTableScroll);
      mirrorEl?.removeEventListener("scroll", onMirrorScroll);
      window.removeEventListener("resize", updateLayout);
    };
  }, [searchResults]);

  const shouldVirtualize = searchResults.length > VIRTUALIZATION_THRESHOLD && scrollMetrics.containerHeight > 0;
  const startIndex = shouldVirtualize
    ? Math.max(0, Math.floor(scrollMetrics.scrollTop / ROW_HEIGHT) - OVERSCAN_COUNT)
    : 0;
  const endIndex = shouldVirtualize
    ? Math.min(
        searchResults.length,
        Math.ceil((scrollMetrics.scrollTop + scrollMetrics.containerHeight) / ROW_HEIGHT) + OVERSCAN_COUNT
      )
    : searchResults.length;
  const visibleRows = shouldVirtualize ? searchResults.slice(startIndex, endIndex) : searchResults;
  const topSpacerHeight = shouldVirtualize ? startIndex * ROW_HEIGHT : 0;
  const bottomSpacerHeight = shouldVirtualize ? (searchResults.length - endIndex) * ROW_HEIGHT : 0;

  const handlePin17Click = (row, i) => {
    const pin = row.pin17 || row.pin13;
    if (!pin) return;
    const oktaTokenStorage = JSON.parse(sessionStorage.getItem("okta-token-storage"));
    const userId = oktaTokenStorage?.accessToken?.claims?.userID;
    setPin17LoadingIndex(i);
    wtyReportApi
      .get("/machine-search/product-details-complete-pin", { serialNumber: pin, userId })
      .then((res) => {
        history.push({
          pathname: `/machine-details/pin/${pin}`,
          state: {
            pinNumber: pin,
            machineSearchType: "machines",
            searchLength: pin.length,
            machineDetail: Array.isArray(res.data) ? res.data[0] : res.data,
            dealerReportRow: row,
          },
        });
      })
      .catch(() => {
        setShowErrorToast(true);
        history.push({
          pathname: `/machine-details/pin/${pin}`,
          state: {
            pinNumber: pin,
            machineSearchType: "machines",
            searchLength: pin.length,
            dealerReportRow: row,
          },
        });
      })
      .finally(() => {
        setPin17LoadingIndex(null);
      });
  };

  const handleClaimsClick = (row, i) => {
    setClaimsLoadingIndex(i);
    wtyReportApi
      .get("/mpr/claims", { ...(row.pin17 ? { pin17: row.pin17 } : { pin13: row.pin13 }) })
      .then((res) => {
        history.push({
          pathname: "/dealer/claims",
          state: { row, claimsResults: Array.isArray(res.data) ? res.data : [] },
        });
      })
      .catch(() => {
        setShowErrorToast(true);
        history.push({
          pathname: "/dealer/claims",
          state: { row, claimsResults: [] },
        });
      })
      .finally(() => {
        setClaimsLoadingIndex(null);
      });
  };

  const handleExportToExcel = () => {
    setExportLoading(true);
    wtyReportApi
      .post("/mpr/export-excel", searchCriteria || (pin ? { pin } : {}))
      .then((res) => {
        setExportModal({ open: true, success: res.status === 200 });
      })
      .catch(() => {
        setExportModal({ open: true, success: false });
      })
      .finally(() => {
        setExportLoading(false);
      });
  };

  return (
    <div style={{ padding: "24px" }}>
      {showErrorToast && <ErrorToast onClose={() => setShowErrorToast(false)} />}
      <style>{`
        .wty-export-modal .modal-content { border: none; box-shadow: none; }
        .wty-export-modal .modal-header { border-bottom: none; }
        .wty-export-modal .modal-footer { border-top: none; padding-top: 0; padding-bottom: 0; }
        .wty-export-modal .modal-body { overflow-y: visible !important; padding-top: 0; padding-bottom: 0; }
      `}</style>
      <Modal
        show={exportModal.open}
        onHide={() => setExportModal({ open: false, success: false })}
        backdrop="static"
        keyboard={false}
        dialogClassName="wty-export-modal"
      >
        <Modal.Header>
          <Modal.Title>{exportModal.success ? "Export initiated" : "Export failed"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {exportModal.success
              ? "Your report export request has been submitted successfully. The report is being generated and will be delivered to your email shortly."
              : "Export failed, please contact support."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setExportModal({ open: false, success: false })}
            style={{ backgroundColor: "#FFDE00", color: "#000" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {(claimsLoadingIndex !== null || pin17LoadingIndex !== null || exportLoading) && (
        <div className="d-flex w-100 justify-content-center align-items-center" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(255,255,255,0.6)", zIndex: 9999 }}>
          <Spinner accessibilityLabel="Loading" className="uxf-spinner-border-lg" animation="border" />
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px", position: "sticky", top: 0, zIndex: 10, backgroundColor: "#fff", paddingBottom: "8px" }}>
        <Button
          variant="tertiary"
          onClick={() => history.push({ pathname: "/machine-search/dealer", state: { activeTab: 1, restoreFilters: true } })}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0", width: "fit-content" }}
        >
          <ArrowBack fontSize="small" />
          Back
        </Button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h4 style={{ margin: 0, fontWeight: 600 }}>Dealer Report</h4>
          <Button
            variant="secondary"
            disabled={exportLoading || searchResults.length === 0}
            onClick={handleExportToExcel}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FileDownload fontSize="small" />
            Download to Excel
          </Button>
        </div>
        {pin && (
          <span style={{ color: "#555", fontSize: "14px" }}>
            PIN: <strong>{pin}</strong>
          </span>
        )}
      </div>

      {searchResults.length === 0 ? (
        <p style={{ color: "#888" }}>No results found.</p>
      ) : (
        <>
          <p style={{ marginBottom: "12px", fontSize: "14px", color: "#555" }}>
            {searchResults.length} record{searchResults.length !== 1 ? "s" : ""} found
          </p>
          <div ref={tableScrollRef} className="report-table-scroll" style={{ border: "1px solid #E0E0E0", borderRadius: "6px", overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 220px)", scrollbarWidth: "none", msOverflowStyle: "none" }}>
            <style>{`.report-table-scroll::-webkit-scrollbar { display: none; }`}</style>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", whiteSpace: "nowrap" }}>
              <thead>
                <tr style={{ backgroundColor: "#F5F5F5", borderBottom: "2px solid #D0D0D0" }}>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        display: VISIBLE_COLUMN_KEYS.has(col.key) ? undefined : "none",
                        padding: "10px 14px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#333",
                        borderRight: "1px solid #E0E0E0",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        backgroundColor: "#F5F5F5",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={COLUMNS.length} style={{ height: `${topSpacerHeight}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
                {visibleRows.map((row, rowOffset) => {
                  const i = startIndex + rowOffset;

                  return (
                  <tr
                    key={i}
                    style={{ borderBottom: "1px solid #E0E0E0", backgroundColor: i % 2 === 0 ? "#fff" : "#FAFAFA" }}
                  >
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          display: VISIBLE_COLUMN_KEYS.has(col.key)
                            ? undefined
                            : "none",
                          padding: "9px 14px",
                          color: "#444",
                          borderRight: "1px solid #E0E0E0",
                        }}
                      >
                        {col.key === "pin17" ? (
                          (row.pin17 || row.pin13) ? (
                            <span
                              onClick={() => pin17LoadingIndex === null && claimsLoadingIndex === null && handlePin17Click(row, i)}
                              style={{ color: "#367C2B", cursor: pin17LoadingIndex === i ? "default" : "pointer", textDecoration: "underline", fontWeight: 500, opacity: pin17LoadingIndex === i ? 0.5 : 1 }}
                            >
                              {pin17LoadingIndex === i ? "Loading..." : (row.pin17 || row.pin13)}
                            </span>
                          ) : ""
                        ) : col.key === "claims" ? (
                          <span
                            onClick={() => claimsLoadingIndex === null && pin17LoadingIndex === null && handleClaimsClick(row, i)}
                            style={{ color: "#367C2B", cursor: claimsLoadingIndex === i ? "default" : "pointer", textDecoration: "underline", fontWeight: 500, opacity: claimsLoadingIndex === i ? 0.5 : 1 }}
                          >
                            {claimsLoadingIndex === i ? "Loading..." : "Link"}
                          </span>
                        ) : col.key === "combinedTerritory" ? (
                          [row.serviceDivision, row.serviceTerritory].filter(Boolean).join("") || ""
                        ) : col.key === "dealerName" ? (
                          getDealerName(row)
                        ) : col.key === "totalWrntySpend" ? (
                          formatWarrantyCost(row.totalWrntySpend)
                        ) : col.key === "newUsedInd" ? (
                          row.newUsedInd === "U" ? "Used" : row.newUsedInd === "N" ? "New" : (row.newUsedInd ?? "")
                        ) : (
                          row[col.key] ?? ""
                        )}
                      </td>
                    ))}
                  </tr>
                  );
                })}
                {bottomSpacerHeight > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={COLUMNS.length} style={{ height: `${bottomSpacerHeight}px`, padding: 0, border: "none" }} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div
        ref={mirrorScrollRef}
        style={{ position: "sticky", bottom: 0, overflowX: "auto", overflowY: "hidden", height: "16px", backgroundColor: "transparent" }}
      >
        <div ref={mirrorInnerRef} style={{ height: "1px" }} />
      </div>
    </div>
  );
};

export default DealerReport;
